import React, { useMemo, useRef, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography, IconButton,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import * as XLSX from "xlsx";
import { useCreateProgramMutation, useGetProgramFolderQuery } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";

/**
 * Import Programs — collapsed single-dialog form.
 *
 * Prior shape was a four-step wizard whose Review step performed no
 * validation. A user who walked a screen labelled Review assumed
 * something was reviewed; nothing was. The wizard shape has been
 * removed deliberately. If a validating Review is built later, the
 * multi-step container becomes defensible again — see the "Import
 * Programs — validating review step" ticket. See also:
 *   - "Import Programs — real file upload"
 *   - "ProgramController::store defaults status to 'draft'"
 *
 * Behaviour:
 *   - Source: Paste (one name per line) OR CSV (paste box; not a real
 *     file picker — see the file-upload ticket).
 *   - Destination folder: dropdown; empty means "row-specified folder
 *     or library root".
 *   - Status: always sent as "waiting" to match ProgramLibrary/Create.
 *     The stranded controller default of "draft" is deliberately not
 *     reached from this surface.
 *   - Row cap: 200. Above that the Import button is disabled with a
 *     clear message. Chunking was rejected — none of the current
 *     defects are throughput defects.
 *   - Skipped counter: empty-name rows are surfaced as a "skipped"
 *     status in the results panel (not silently dropped).
 *   - Folder-not-found: creates at library root and records the
 *     mismatch as an inline note on that row's result.
 *   - Every created row carries import_source='program_importer' so a
 *     future audit can prove which rows came from here.
 */

const ROW_CAP = 200;
const IMPORT_STATUS = "waiting";        // matches ProgramLibrary/Create
const IMPORT_SOURCE = "program_importer";
const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB — matches the 200-row cap in practice
const ACCEPTED_MIME_EXT = [
  ".csv", ".xls", ".xlsx",
  "text/csv",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
].join(",");

// CSV template header must match the parser's expected column order in
// `rows` below: Program Name, Category, Folder, Description. When the
// template is updated, keep this header in lockstep with the extractor.
const TEMPLATE_CSV = [
  "Program Name,Category,Folder,Description",
  "Grade 2 Word Problems,Skill Acquisition,Math,Sample program",
  "Waiting Skills,Behaviour,Behaviour Reduction,Increasing tolerance for waiting",
  "Requesting,Language,Communication,Teaching requesting skills",
].join("\n") + "\n";

type Row = {
  programName: string;
  category?: string;
  folderName?: string;
  description?: string;
  _skipped?: boolean;                    // empty-name marker
  _folderMissing?: boolean;              // CSV named a folder that doesn't exist
};
type Result = {
  row: Row;
  status: "created" | "skipped" | "failed";
  message?: string;
};

export default function ImportProgramsDialog({
  open, onClose,
}: { open: boolean; onClose: () => void }) {
  const { showMessage, showErrorMessage } = useNotifier();
  const [createProgram] = useCreateProgramMutation();
  const { data: folderData } = useGetProgramFolderQuery({});
  const folders: any[] = Array.isArray(folderData?.data) ? (folderData!.data as any[]) : [];

  const [source, setSource] = useState<"paste" | "csv" | "file">("paste");
  const [pasted, setPasted] = useState("");
  const [csvText, setCsvText] = useState("");
  const [destFolder, setDestFolder] = useState<string>("");
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<Result[] | null>(null);
  // File-source state — the picked filename is displayed as a chip
  // while parsing runs. The parsed content is fed into `csvText` so
  // the downstream row derivation + duplicate detection + per-row
  // create call are literally the same code path as CSV paste.
  const [fileName, setFileName] = useState<string>("");
  const [fileError, setFileError] = useState<string>("");
  const [parsing, setParsing]   = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const reset = () => {
    setSource("paste"); setPasted(""); setCsvText("");
    setDestFolder(""); setResults(null); setRunning(false);
    setFileName(""); setFileError(""); setParsing(false); setDragOver(false);
  };
  const handleClose = () => { onClose(); reset(); };

  const clearFile = () => {
    setFileName(""); setFileError(""); setCsvText("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  /**
   * Parse an uploaded CSV / XLSX / XLS into a CSV string, then set it
   * as `csvText` so the existing row derivation runs. Consolidating on
   * one CSV string keeps a single source of truth for validation,
   * folder mapping, and duplicate handling — no separate "file import"
   * business logic ever exists.
   */
  const ingestFile = async (file: File | null) => {
    setFileError("");
    if (!file) return;
    const nameLower = file.name.toLowerCase();
    const isCsv  = nameLower.endsWith(".csv")  || file.type === "text/csv";
    const isXls  = nameLower.endsWith(".xls")  || file.type === "application/vnd.ms-excel";
    const isXlsx = nameLower.endsWith(".xlsx") || file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    if (!isCsv && !isXls && !isXlsx) {
      setFileError("Unsupported file type. Please upload a .csv, .xlsx, or .xls file.");
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setFileError("File is larger than 5 MB. Please split it into smaller files.");
      return;
    }
    setParsing(true);
    setFileName(file.name);
    try {
      if (isCsv) {
        const text = await file.text();
        setCsvText(text);
      } else {
        const buf = await file.arrayBuffer();
        // SheetJS reads the first sheet and emits CSV rows. This keeps
        // downstream logic exactly identical to the CSV-paste path;
        // the CSV parser below normalises headers + folder mapping.
        const wb = XLSX.read(buf, { type: "array" });
        const firstSheetName = wb.SheetNames[0];
        if (!firstSheetName) throw new Error("Workbook has no sheets.");
        const csv = XLSX.utils.sheet_to_csv(wb.Sheets[firstSheetName]);
        setCsvText(csv);
      }
    } catch (e: any) {
      setFileError(e?.message || "Failed to read the file.");
      setFileName("");
    } finally {
      setParsing(false);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "program-import-template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const flatFolders = useMemo(() => {
    const out: Array<{ id: string; label: string; nameLower: string }> = [];
    const walk = (list: any[], depth: number) => {
      for (const f of list ?? []) {
        out.push({
          id: String(f.id),
          label: `${"- ".repeat(depth)}${f.folderName}`,
          nameLower: String(f.folderName ?? "").toLowerCase(),
        });
        if (f.children?.length) walk(f.children, depth + 1);
      }
    };
    walk(folders, 0);
    return out;
  }, [folders]);

  const findFolderIdByName = (name?: string): string | null => {
    if (!name) return null;
    const hit = flatFolders.find((f) => f.nameLower === name.toLowerCase());
    return hit ? hit.id : null;
  };

  /**
   * Parse the current source into a Row[]. Empty-name rows are RETAINED
   * with _skipped=true so the results panel can surface them; they are
   * not silently dropped.
   */
  const rows = useMemo((): Row[] => {
    // File source shares the CSV path — the file's contents were
    // already parsed into `csvText` by ingestFile above.
    if (source === "paste") {
      return pasted.split(/\r?\n/).map((l) => l.trim())
        .filter((l) => l.length > 0)   // blank lines never intended to be rows — legitimate to drop
        .map((programName) => ({ programName }));
    }
    // CSV branch — preserves empty-name rows as _skipped markers.
    const parseCsvLine = (line: string) => {
      const out: string[] = []; let cur = ""; let inQuote = false;
      for (let i = 0; i < line.length; i++) {
        const c = line[i];
        if (c === '"') inQuote = !inQuote;
        else if (c === "," && !inQuote) { out.push(cur); cur = ""; }
        else cur += c;
      }
      out.push(cur);
      return out.map((s) => s.trim().replace(/^"|"$/g, ""));
    };
    const lines = csvText.split(/\r?\n/).map((l) => l.trim());
    // Blank lines (fully-empty) never intended to be rows.
    const materialLines = lines.filter((l) => l.length > 0);
    if (materialLines.length === 0) return [];
    let start = 0;
    if (/program\s*name/i.test(materialLines[0])) start = 1;
    return materialLines.slice(start).map((line) => {
      const cells = parseCsvLine(line);
      const name = cells[0] ?? "";
      const folderName = (cells[2] ?? "").trim() || undefined;
      return {
        programName: name,
        category:    cells[1] ?? undefined,
        folderName,
        description: cells[3] ?? undefined,
        _skipped: name.length === 0,
        _folderMissing: !destFolder && !!folderName && findFolderIdByName(folderName) === null,
      };
    });
  }, [source, pasted, csvText, destFolder, flatFolders]);

  const importable = rows.filter((r) => !r._skipped);
  const skippedCount = rows.length - importable.length;
  const overCap = importable.length > ROW_CAP;

  const canImport = !running && importable.length > 0 && !overCap;

  const runImport = async () => {
    setRunning(true);
    const out: Result[] = [];

    // Empty-name rows land in the results as skipped first, so their
    // presence is visible even if the run halts.
    for (const r of rows) {
      if (r._skipped) {
        out.push({ row: r, status: "skipped", message: "empty program name" });
      }
    }

    for (const r of importable) {
      const rowFolderId = destFolder || findFolderIdByName(r.folderName) || null;
      try {
        const res = await createProgram({
          programName: r.programName,
          category: r.category ?? null,
          description: r.description ?? null,
          folderId: rowFolderId,
          status: IMPORT_STATUS,
          import_source: IMPORT_SOURCE,
        } as any).unwrap();

        if (res?.status) {
          const note = (!destFolder && r.folderName && !rowFolderId)
            ? `created at library root - folder "${r.folderName}" not found`
            : undefined;
          out.push({ row: r, status: "created", message: note });
        } else {
          out.push({ row: r, status: "failed", message: res?.message || "backend refused" });
        }
      } catch (e: any) {
        out.push({ row: r, status: "failed", message: e?.data?.message || e?.message || "unknown error" });
      }
    }
    setResults(out);
    setRunning(false);

    const created = out.filter((r) => r.status === "created").length;
    const failed  = out.filter((r) => r.status === "failed").length;
    const skipped = out.filter((r) => r.status === "skipped").length;

    if (created > 0) {
      const detail = [
        skipped ? `${skipped} skipped` : null,
        failed  ? `${failed} failed`  : null,
      ].filter(Boolean).join(", ");
      showMessage(`Imported ${created} program(s)` + (detail ? ` (${detail}).` : "."));
    }
    if (created === 0 && failed > 0) {
      showErrorMessage(`Import failed. ${failed} error(s).`);
    }
  };

  return (
    <Dialog open={open} onClose={running ? undefined : handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Import Programs</DialogTitle>
      <DialogContent dividers>
        {!results ? (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              New programs are created with status <strong>Waiting</strong>.
            </Typography>

            <Stack direction="row" spacing={2}>
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel>Source</InputLabel>
                <Select label="Source" value={source} onChange={(e) => setSource(e.target.value as any)}>
                  <MenuItem value="paste">Paste program names (one per line)</MenuItem>
                  <MenuItem value="csv">CSV text (paste)</MenuItem>
                  <MenuItem value="file">Upload file (.csv / .xlsx / .xls)</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Destination folder</InputLabel>
                <Select label="Destination folder" value={destFolder}
                  onChange={(e) => setDestFolder(e.target.value)}>
                  <MenuItem value="">Library root (or CSV-specified)</MenuItem>
                  {flatFolders.map((f) => <MenuItem key={f.id} value={f.id}>{f.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Stack>

            {source === "paste" && (
              <TextField
                fullWidth multiline minRows={8}
                label="One program name per line"
                value={pasted} onChange={(e) => setPasted(e.target.value)}
                placeholder={"Manding - requesting items\nTacting - labelling items\nFine motor - grasping"}
              />
            )}
            {source === "csv" && (
              <TextField
                fullWidth multiline minRows={8}
                label="CSV rows (header optional)"
                value={csvText} onChange={(e) => setCsvText(e.target.value)}
                placeholder={"Program Name,Category,Folder,Description\nGrade 2 Word Problems,Skill Acquisition,Math,Sample program"}
              />
            )}
            {source === "file" && (
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Upload a CSV or Excel file. Columns: Program Name, Category, Folder, Description. Maximum 5 MB.
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<DownloadOutlinedIcon fontSize="small" />}
                    onClick={downloadTemplate}
                    sx={{ textTransform: "none", fontWeight: 700, flexShrink: 0 }}
                  >
                    Download template
                  </Button>
                </Box>

                <Box
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
                  onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); }}
                  onDrop={(e) => {
                    e.preventDefault(); e.stopPropagation(); setDragOver(false);
                    const f = e.dataTransfer.files?.[0] ?? null;
                    ingestFile(f);
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); fileInputRef.current?.click(); } }}
                  sx={{
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                    gap: 1,
                    py: 4, px: 3,
                    border: (t) => `2px dashed ${dragOver ? t.palette.primary.main : t.palette.divider}`,
                    borderRadius: 2,
                    bgcolor: (t) => dragOver ? "rgba(29,78,216,0.04)" : "transparent",
                    cursor: "pointer",
                    transition: "background-color 150ms, border-color 150ms",
                    "&:hover": { borderColor: (t) => t.palette.primary.main, bgcolor: "rgba(29,78,216,0.03)" },
                  }}
                >
                  <CloudUploadOutlinedIcon sx={{ fontSize: 36, color: "text.secondary" }} />
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Drag &amp; drop a file here
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    or click to choose a file
                  </Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5 }}>
                    Accepted: .csv, .xlsx, .xls · max 5 MB
                  </Typography>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_MIME_EXT}
                    onChange={(e) => ingestFile(e.target.files?.[0] ?? null)}
                    style={{ display: "none" }}
                  />
                </Box>

                {parsing && (
                  <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={14} />
                    <Typography variant="caption" color="text.secondary">Reading file…</Typography>
                  </Box>
                )}

                {fileName && !parsing && (
                  <Box sx={{
                    mt: 1.5, p: 1.25, borderRadius: 1,
                    border: (t) => `1px solid ${t.palette.divider}`,
                    display: "flex", alignItems: "center", gap: 1.25,
                  }}>
                    <DescriptionOutlinedIcon sx={{ color: "text.secondary" }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {fileName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {rows.length} row{rows.length === 1 ? "" : "s"} found
                      </Typography>
                    </Box>
                    <Button size="small" onClick={() => fileInputRef.current?.click()} sx={{ textTransform: "none", fontWeight: 700 }}>
                      Replace
                    </Button>
                    <IconButton size="small" onClick={clearFile} aria-label="Remove file">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}

                {fileError && (
                  <Alert severity="error" sx={{ mt: 1.5 }}>{fileError}</Alert>
                )}
              </Box>
            )}

            {(importable.length > 0 || skippedCount > 0) && (
              <Alert severity={overCap ? "warning" : "info"}>
                Will import <strong>{Math.min(importable.length, ROW_CAP)}</strong> program(s).
                {skippedCount > 0 && ` ${skippedCount} row(s) had no program name and will be skipped.`}
                {overCap && ` ${importable.length - ROW_CAP} row(s) over the ${ROW_CAP}-row cap - split the paste and re-run.`}
              </Alert>
            )}
          </Stack>
        ) : (
          <Box>
            <Alert severity={results.every((r) => r.status === "created") ? "success" : "warning"} sx={{ mb: 1.5 }}>
              Import finished. {results.filter((r) => r.status === "created").length} created,{" "}
              {results.filter((r) => r.status === "skipped").length} skipped,{" "}
              {results.filter((r) => r.status === "failed").length} failed.
            </Alert>
            <Box sx={{ maxHeight: 320, overflow: "auto", border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 1 }}>
              {results.map((r, i) => (
                <Box key={i} sx={{ px: 1.5, py: 1, borderBottom: (t) => `1px solid ${t.palette.divider}`, display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                  <Chip size="small"
                    color={r.status === "created" ? "success" : r.status === "failed" ? "error" : "default"}
                    label={r.status} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {r.row.programName || "(empty)"}
                  </Typography>
                  {r.message && (
                    <Typography variant="caption" color="text.secondary">- {r.message}</Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={running}>{results ? "Close" : "Cancel"}</Button>
        {!results && (
          <Button variant="contained" onClick={runImport} disabled={!canImport}
            startIcon={running ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : undefined}>
            {running ? "Importing..." : `Import ${Math.min(importable.length, ROW_CAP)}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
